import React, { Dispatch, SetStateAction, useMemo } from "react";
import { Flex, Modal, Table } from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import i18next from "i18next";
import styled from "styled-components";

import { formatCurrency } from "components/Utils/agGrid/valueFormatters";
import {
  BSCoreServicesDtoMarketplaceOrderDto,
  BSCoreServicesDtoMarketplaceOrderProductDto,
  BSCoreWebRequestsResponsesDistributorsDistributorDepotResponse,
} from "api/reactQuery/querySchemas";
import Card from "components/Card/Card";
import TextArea from "components/TextArea";

import WarehouseCard from "./components/WarehouseCard";

type Props = {
  isOpen: boolean;
  onSubmit: () => Promise<void>;
  isLoading: boolean;
  onCancel: () => void;
  data: BSCoreServicesDtoMarketplaceOrderDto;
  setOrderComment: Dispatch<SetStateAction<string>>;
  selectedDepots: BSCoreWebRequestsResponsesDistributorsDistributorDepotResponse[];
};

const OrderPreviewModal = ({
  isOpen,
  onSubmit,
  isLoading,
  onCancel,
  data,
  setOrderComment,
  selectedDepots,
}: Props) => {
  const cols: ColumnsType = useMemo(
    () => [
      {
        dataIndex: "name",
        key: "name",
        title: i18next.t("order_preview_modal_name"),
      },
      {
        dataIndex: "expiryDate",
        key: "expiryDate",
        title: i18next.t("order_preview_expiry_date"),
      },
      {
        dataIndex: "productPrice",
        key: "productPrice",
        title: i18next.t("order_preview_product_price"),
        render: (value: number) => formatCurrency(value, 4, 4),
      },
      {
        dataIndex: "unitsInPallet",
        key: "unitsInPallet",
        title: i18next.t("order_preview_units_in_pallet"),
      },
      {
        dataIndex: "productVolumeInHL",
        key: "productVolumeInHL",
        title: i18next.t("product_volume_in_hl"),
      },
      {
        dataIndex: "productWeight",
        key: "productWeight",
        title: i18next.t("product_weight"),
      },
      {
        dataIndex: "orderedQuantitiesInPallets",
        key: "orderedQuantitiesInPallets",
        title: i18next.t("ordered_quantities_in_pallets"),
      },
      {
        dataIndex: "orderedQuantitiesInUnits",
        key: "orderedQuantitiesInUnits",
        title: i18next.t("ordered_quantites_in_units"),
      },
      {
        dataIndex: "orderedUnits",
        key: "orderedUnits",
        title: i18next.t("ordered_units"),
      },
      {
        dataIndex: "totalBeerAndPackagingCostWithVat",
        key: "totalBeerAndPackagingCostWithVat",
        title: i18next.t("total_beer_and_packaging_cost_with_vat"),
        render: (value: number) => formatCurrency(value, 2, 2),
      },
    ],
    []
  );

  const isBonusOnInvoiceProduct = (product: BSCoreServicesDtoMarketplaceOrderProductDto) =>
    product.isSplitProduct &&
    product.isBonusInvoiceProduct &&
    product.parentId !== null &&
    product.id;

  const isNcpCompensationProduct = (product: BSCoreServicesDtoMarketplaceOrderProductDto) =>
    product.isSplitProduct &&
    product.isNcpCompensationProduct &&
    product.parentId !== null &&
    product.id;

  const isOnInvoiceProduct = (product: BSCoreServicesDtoMarketplaceOrderProductDto) =>
    product.isInvoiceProduct &&
    product.parentId === null &&
    !product.isSplitProduct &&
    product.id;

  const isMultiPackProduct = (product: BSCoreServicesDtoMarketplaceOrderProductDto) =>
    product.isMultiPack &&
    !product.isBonusInvoiceProduct &&
    !product.isNcpCompensationProduct &&
    product.id;

  const isProduct = (product: BSCoreServicesDtoMarketplaceOrderProductDto) =>
    (
      (!product.isMultiPack && !product.isInvoiceProduct) ||
      (product.isSplitProduct && product.parentId !== null)
    ) &&
    product.id &&
    product.productTypeId !== 1 &&
    product.productTypeId !== 2 &&
    !product.isBonusInvoiceProduct &&
    !product.isNcpCompensationProduct;

  const isGasProduct = (product: BSCoreServicesDtoMarketplaceOrderProductDto) =>
    product.id &&
    (product.productTypeId === 1 || product.productTypeId === 2);

  const tables = [
    {
      label: i18next.t("bonus_oninvoice"),
      filterFn: isBonusOnInvoiceProduct,
      checkVisible: (products: BSCoreServicesDtoMarketplaceOrderProductDto[]) => products?.some(isBonusOnInvoiceProduct),
    },
    {
      label: i18next.t("ncp_compensation"),
      filterFn: isNcpCompensationProduct,
      checkVisible: (products: BSCoreServicesDtoMarketplaceOrderProductDto[]) => products?.some(isNcpCompensationProduct),
    },
    {
      label: i18next.t("oninvoice"),
      filterFn: isOnInvoiceProduct,
      checkVisible: (products: BSCoreServicesDtoMarketplaceOrderProductDto[]) => products?.some(isOnInvoiceProduct),
    },
    {
      label: i18next.t("multipack"),
      filterFn: isMultiPackProduct,
      checkVisible: (products: BSCoreServicesDtoMarketplaceOrderProductDto[]) => products?.some(isMultiPackProduct),
    },
    {
      label: i18next.t("products"),
      filterFn: isProduct,
      checkVisible: (products: BSCoreServicesDtoMarketplaceOrderProductDto[]) => products?.some(isProduct),
    },
    {
      label: i18next.t("gas_products"),
      filterFn: isGasProduct,
      checkVisible: (products: BSCoreServicesDtoMarketplaceOrderProductDto[]) => products?.some(isGasProduct),
    },
  ];

  return (
    <Modal
      open={isOpen}
      onOk={onSubmit}
      confirmLoading={isLoading}
      onCancel={onCancel}
      width="90%"
      okText={i18next.t("send_order")}
      cancelText={i18next.t("back_button")}
    >
      <Heading>{i18next.t("order_overview")}</Heading>
      <Section>
        <TitleAndDateWrapper>
          <SectionTitle>{i18next.t("order_placement")}</SectionTitle>
          <p>
            {i18next.t("delivery_date")}{" "}
            <span>{dayjs(data?.deliveryDate).format("DD.MM.YYYY")}</span>
          </p>
        </TitleAndDateWrapper>
        <WarehouseCardWrapper>
          {selectedDepots.map((depot) => (
            <WarehouseCard key={depot.id} depot={depot} />
          ))}
        </WarehouseCardWrapper>
      </Section>

      <Section>
        <SectionTitle>{i18next.t("products")}</SectionTitle>
        {data?.childOrders?.map((order) => {
          return (
            <div key={order.id}>
              <DepotName>{order.depotName}</DepotName>
              {tables.map(
                (table) =>
                  table.checkVisible(order.products) && (
                    <div key={table.label}>
                      <DepotName>{table.label}</DepotName>
                      <Table
                        columns={cols}
                        pagination={false}
                        dataSource={order?.products
                          ?.filter(table.filterFn)
                          .map((product) => ({
                            expiryDate: product.expiryDate
                              ? dayjs(product.expiryDate).format("DD.MM.YY")
                              : "",
                            isBonusInvoiceProduct:
                              product.isBonusInvoiceProduct,
                            key: product.feTableId.toString(),
                            name: product.productName,
                            orderedQuantitiesInPallets:
                              product.orderedQuantitiesInPallets,
                            orderedQuantitiesInUnits:
                              product.orderedQuantitiesInUnits,
                            orderedUnits: product.orderedUnits,
                            parentId: product.parentId,
                            productPrice: product.productPrice,
                            productVolumeInHL: product.productVolumeInHL,
                            productWeight: product.productWeight,
                            totalBeerAndPackagingCostWithVat:
                              product.totalBeerAndPackagingCostWithVat,
                            unitsInPallet: product.productUnitsInPallet,
                          }))}
                      />
                    </div>
                  )
              )}

              <CardWrapper>
                <Card
                  title={i18next.t("ttl_weight")}
                  value={order.total.ttlWeight}
                />
                <Card
                  title={i18next.t("total_beer_cost_with_vat_in_bgn")}
                  value={order.total.ttlBeerCostWithVAT}
                  isBlueCard={true}
                />
              </CardWrapper>
            </div>
          );
        })}
      </Section>

      {!!data?.total && (
        <Section>
          <SectionTitle>{i18next.t("total")}</SectionTitle>

          <Flex style={{ gap: 10, width: "100%" }} justify="space-between">
            <Card
              title={i18next.t("ttl_pallets")}
              value={data.total.ttlPallets}
              width="25%"
            />
            <Card
              title={i18next.t("ttl_pallet_count_licensed")}
              value={data.total.ttlPalletCountLicensedType}
              width="25%"
            />
            <Card
              title={i18next.t("ttl_pallet_count_industrial")}
              value={data.total.ttlPalletCountIndustrialType}
              width="25%"
            />
            <Card
              title={i18next.t("ttl_hl")}
              value={data.total.ttlVolumeInHL}
              width="25%"
            />
          </Flex>

          <Flex
            style={{ gap: 10, marginTop: 10, width: "100%" }}
            justify="space-between"
          >
            <Card
              title={i18next.t("ttl_cost_without_vat")}
              value={data.total.ttlBeerCostWithoutVAT}
              width="25%"
            />
            <Card
              title={i18next.t("total_beer_and_packaging_cost_with_vat")}
              value={data.total.ttlBeerWithVATAndPackagingCost}
              width="25%"
            />
            <Card
              title={i18next.t("total_packaging_cost_in_bgn")}
              value={data.total.ttlPackagingCost}
              width="25%"
            />
            <Card
              title={i18next.t("ttl_weight")}
              value={data.total.ttlWeight}
              width="25%"
            />
          </Flex>
        </Section>
      )}

      <Section>
        <SectionTitle>{i18next.t("order_comment")}</SectionTitle>

        <TextArea
          placeholder={i18next.t("order_comment_placeholder")}
          onChange={(e) => setOrderComment(e.target.value)}
          maxLength={140}
          maxLines={2}
          style={{ resize: "none" }}
        />
      </Section>
    </Modal>
  );
};

export default OrderPreviewModal;

const Section = styled.div`
  margin-top: 38px;
`;

const SectionTitle = styled.h2`
  color: #6d6d6d;
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 10px;
`;

const WarehouseCardWrapper = styled.div`
  display: flex;
  gap: 20px;
`;

const Heading = styled.h1`
  color: #363636;
  font-size: 32px;
  font-weight: 400;
`;

const TitleAndDateWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;

  & > p {
    color: #6d6d6d;

    & > span {
      font-weight: bold;
    }
  }
`;

const DepotName = styled.h3`
  font-weight: 400;
  font-size: 18px;
  color: #181818;
  margin: 20px 0;
`;

const CardWrapper = styled.div`
  margin-top: 15px;
  display: flex;
  justify-content: flex-end;
  gap: 15px;
`;
